import { createContext, useContext } from 'react';
import type {
  AgentRef,
  CannedResponse,
  CrmCapabilities,
  Inbox,
  Label,
  TemplateBlock,
} from '../../api/types';
import type { CommsSource } from './source';

export interface CommsState {
  source: CommsSource;
  /** All tenant inboxes — loaded once, the module's channel backbone. */
  inboxes: Inbox[];
  inboxesLoading: boolean;
  /** A manual channel-status refresh is in flight (the list stays on screen). */
  inboxesRefreshing: boolean;
  /** null → inboxes failed to load with a permission error (module locked). */
  accessDenied: boolean;
  agents: AgentRef[];
  labels: Label[];
  canned: CannedResponse[];
  /**
   * The dashboard's reusable email blocks, rendered for this tenant. Empty
   * whenever dispatch has not granted the component table — every picker that
   * offers them hides itself instead.
   */
  templateBlocks: TemplateBlock[];
  /** The signed-in admin's dispatch dash_board row, matched by email. */
  me: AgentRef | null;
  /**
   * Where this browser files what an agent leaves half-typed — one bucket per
   * tenant and per agent (see `screens/comms/drafts.ts` for why both). Known
   * before the first thread renders, unlike [me], which waits on /agents: a
   * scope that arrived late would restore a draft into a slot nothing had
   * saved to.
   */
  draftScope: string;
  /**
   * The signed-in agent's grants from GET /meta, or null while loading (or if
   * the lookup failed). Prefer the helpers below over reading this directly —
   * they carry the permissive-when-unknown rule.
   */
  capabilities: CrmCapabilities | null;
  /** Can this agent send from this inbox? */
  canReply: (inboxId: number) => boolean;
  /** Can this agent place or take calls on this inbox? */
  canCall: (inboxId: number) => boolean;
  /** Why the composer is disabled for this inbox, as user-facing copy. */
  replyBlocked: (inboxId: number | null) => string | null;
  /** True when voice is available to this agent anywhere. */
  callsAvailable: boolean;
  /**
   * True when the agent cannot send ANYWHERE — drives the module-level read-only
   * banner. Normally answered by /meta up front; it also latches if a send-gated
   * call bounces with 1036, which covers a grant revoked mid-session before the
   * socket's access.changed lands.
   */
  sendDenied: boolean;
  /**
   * True when no channel is readable BECAUSE the agent was denied every one,
   * rather than because the tenant has connected none. The two look identical
   * from an empty module and need opposite words.
   */
  noChannelAccess: boolean;
  noteSendDenied: () => void;
  reloadInboxes: () => void;
  /**
   * Re-read the label catalog. Applying a title nobody has used before CREATES
   * it (there is no other way to make one), so the picker that did it has to
   * say so — otherwise the new label is offered on no other thread until the
   * whole module is refreshed.
   */
  reloadLabels: () => void;
  /**
   * Re-read the whole module: the catalogs above, the agent's grants, and every
   * view's own data. The module bar cannot reach into a view's state, so views
   * answer the bump on [refreshNonce] themselves — see `useModuleRefresh`.
   */
  refreshAll: () => void;
  /** Incremented by [refreshAll]. Starts at 0; a view must ignore its first value. */
  refreshNonce: number;
  /** The module-level reads are in flight (held past the response, as everywhere). */
  refreshingAll: boolean;
}

export const CommsContext = createContext<CommsState | null>(null);

export function useComms(): CommsState {
  const ctx = useContext(CommsContext);
  if (!ctx) throw new Error('useComms must be used inside CommsProvider');
  return ctx;
}
